<script setup>
  import BaseDialog from "@/base-components/Dialog/BaseDialog.vue";
  import Button from "@/base-components/Button";
  import { uri } from "@/constants/config";
  import InputText from "@/base-components/Form/InputText.vue";
  import { Form } from "vee-validate";
  import BaseInputSelect from "@/base-components/Form/BaseInputSelect.vue";
  import { onMounted, ref, watch } from "vue";
  import { object } from "yup";
  import { useFetch } from "@/composables/useFetch";
  import useValidate from "@/composables/useValidate.js";
  import { useI18n } from "vue-i18n";
  import InputTextarea from "@/base-components/Form/InputTextarea.vue";
  import Multiselect from "vue-multiselect";

  const props = defineProps({
    show: {
      type: Boolean,
      default: false,
    },
    role: {
      type: String,
      default: "admin",
    },
  });
  const { t: $t } = useI18n();
  const emit = defineEmits(["close", "onSuccess"]);
  const validationSchema = object(
    useValidate(["description", "department", "user", "title"])
  );

  function close() {
    emit("close");
  }

  function onSuccess() {
    try {
    } catch (error) {
    } finally {
    }
    emit("onSuccess");
  }

  const requestParams = ref({
    title: null,
    description: null,
    user: null,
    service: null,
    department: null,
    priority: null,
    file: null,
  });
  const slug = ref(null);
  const data = null;
  const date = "";
  const isLoadingFlag = ref(true);
  const departmentList = ref([]);
  const userList = ref([]);
  const serviceList = ref([]);
  const priorityList = [
    { id: 0, name: "کم" },
    { id: 1, name: "معمولی" },
    { id: 2, name: "زیاد" },
    { id: 3, name: "فوری" },
  ];

  async function trySendTicket() {
    isLoadingFlag.value = true;
    const form = new FormData();
    form.append("title", requestParams.value.title);
    form.append("ownerId", requestParams.value.user.id);
    form.append("file", requestParams.value.file);
    form.append("description", requestParams.value.description);
    form.append("departmentId", requestParams.value.department?.id ?? -1);
    form.append("priority", requestParams.value.priority?.id ?? 0);
    form.append("category", "IAAS");
    form.append("serviceId", requestParams.value.service?.id ?? 0);
    form.append("serviceName", requestParams.value.service?.name ?? "-");
    if (props.role === "cloud")
      form.append("domainId", requestParams.value?.domainId ?? "");

    console.log(form);

    const urls = {
      admin: `${uri.admin.support.ticket.open}`,
      cloud: `${uri.admin.cloud.support.ticket.open}`,
    };
    const url = urls[props.role];

    try {
      const { data, error } = await useFetch(url, { method: "post", form });
      if (data) {
        emit("onSuccess");
      }
    } catch (err) {
      console.log(err);
    } finally {
      isLoadingFlag.value = false;
    }
  }

  const getDepartmentList = async () => {
    isLoadingFlag.value = true;
    let response = [];

    const urls = {
      admin: `${uri.admin.support.department.list}`,
      cloud: `${uri.admin.cloud.support.department.list}`,
    };
    const url = urls[props.role];

    try {
      const { data, error } = await useFetch(url, { method: "GET" });
      if (data) {
        response = data;
      }
    } catch (err) {
      console.log(err);
    }
    return response.data;
  };

  const getUserList = async () => {
    isLoadingFlag.value = true;
    let response = [];

    const urls = {
      admin: `${uri.admin.user.list}?perPage=1000`,
      cloud: `${uri.admin.cloud.user.list}?perPage=1000`,
    };
    const url = urls[props.role];

    try {
      const { data, error } = await useFetch(url, { method: "GET" });
      if (data) {
        response = data;
      }
    } catch (err) {
      console.log(err);
    }
    return response.data;
  };

  const fetchData = async () => {
    isLoadingFlag.value = true;
    try {
      const [departmentData, userData] = await Promise.all([
        getDepartmentList(),
        getUserList(),
      ]);
      departmentList.value = departmentData;
      userList.value = userData;
    } catch (error) {
      console.error(error);
    } finally {
      isLoadingFlag.value = false;
    }
  };
  const fileInput = ref();

  function setFile() {
    requestParams.value.file = fileInput.value.files[0];
  }

  function clearFile() {
    fileInput.value.value = null;
    requestParams.value.file = null;
  }

  async function searchForUserServices(user) {
    serviceList.value = [];
    requestParams.value.service = null;
    if (!user) return;

    const urls = {
      admin: `${uri.admin.iaas.instance.list}`,
      cloud: `${uri.admin.cloud.iaas.instance.list}`,
    };
    const url = urls[props.role];

    const { data } = await useFetch(url, {
      params: { ownerId: user.id },
    });
    serviceList.value = data?.data ?? [];
  }

  function getServiceStatus(status) {
    const statusList = {
      0: "draft",
      2: "activeRequest",
      5: "active",
      10: "suspend",
      [-1]: "delete",
    };
    const txt = statusList[status] || "unknown";
    return $t(`title.${txt}`);
  }

  function serviceCustomLabel(data) {
    return data.name + " - " + getServiceStatus(data.status);
  }

  watch(() => requestParams.value.user, searchForUserServices);

  onMounted(() => {
    fetchData();
    if (props.role === "cloud") {
      getCloudDomainList();
    }
  });

  const cloudList = ref([]);
  const loadingFlagCloudDomainList = ref(false);
  const cloudSelected = ref(null);
  async function getCloudDomainList() {
    loadingFlagCloudDomainList.value = true;

    try {
      // Await the result of useFetch

      const { data, error } = await useFetch(uri.admin.cloud.list, {
        method: "GET",
      });
      if (data) {
        cloudList.value = data;
      }
    } catch (err) {
      console.log(err);
    } finally {
      loadingFlagCloudDomainList.value = false;
    }
  }
  function domainSelected(domain) {
    requestParams.value.domainId = domain.id;
  }
</script>

<template>
  <section>
    <BaseDialog
      :size="'lg'"
      staticBackdrop
      :open="show"
      :has-footer="true"
      :title="$t('title.add-request')"
      @close="close"
    >
      <div class="rounded-md px-2 py-2 w-full max-h-[78vh] overflow-auto mb-5">
        <Form
          id="myForm"
          :validationSchema="validationSchema"
          @submit="trySendTicket"
        >
          <div class="grid grid-cols-2 gap-3">
            <div class="col-span-2 md:col-span-2 lg:col-span-2 xl:col-span-2">
              <FreeStyleShimmer
                height="38px"
                width="100%"
                class="mt-10"
                v-if="isLoadingFlag"
              />
              <div v-else>
                <BaseInputSelect
                  :has-label="true"
                  :title="$t('label.user')"
                  class="primary"
                  v-model="requestParams.user"
                  name="user"
                  deselect-label=""
                  :is-multiple="false"
                  track-by="email"
                  label="email"
                  :options="userList"
                  :searchable="false"
                  :allow-empty="false"
                >
                </BaseInputSelect>
              </div>
            </div>
            <div class="col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1">
              <FreeStyleShimmer
                height="38px"
                width="100%"
                class="mt-10"
                v-if="isLoadingFlag"
              />
              <InputText
                v-else
                name="title"
                :has-label="true"
                :label="$t('label.title')"
                v-model="requestParams.title"
                id="input-wizard-3"
                type="text"
              />
            </div>
            <div class="col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1">
              <FreeStyleShimmer
                height="38px"
                width="100%"
                class="mt-10"
                v-if="isLoadingFlag"
              />
              <div v-else>
                <BaseInputSelect
                  :has-label="true"
                  :title="$t('label.department')"
                  class="primary"
                  v-model="requestParams.department"
                  name="department"
                  deselect-label=""
                  :is-multiple="false"
                  track-by="name"
                  label="name"
                  :options="departmentList"
                  :searchable="false"
                  :allow-empty="false"
                >
                </BaseInputSelect>
              </div>
            </div>
            <div
              v-if="role === 'cloud'"
              class="col-span-2 md:col-span-2 lg:col-span-2"
            >
              <!--Begin Domain Name -->
              <div class="intro-y">
                <div class="mb-1">{{ $t("title.domain") }}</div>
                <Multiselect
                  class="primary mb-1.5"
                  v-model="cloudSelected"
                  name="id"
                  deselect-label=""
                  :is-multiple="false"
                  track-by="id"
                  :options="cloudList"
                  :searchable="true"
                  :allow-empty="true"
                  :is-preselect-first="false"
                  :loading="loadingFlagCloudDomainList"
                  :internal-search="false"
                  @select="domainSelected"
                  :customLabel="
                    (data) => {
                      return data.displayName;
                    }
                  "
                >
                </Multiselect>
              </div>
              <!--End Domain Name -->
            </div>
            <div class="col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1">
              <FreeStyleShimmer
                height="38px"
                width="100%"
                class="mt-10"
                v-if="isLoadingFlag"
              />
              <div v-else>
                <BaseInputSelect
                  :has-label="true"
                  :title="$t('label.service')"
                  class="primary"
                  v-model="requestParams.service"
                  name="service"
                  deselect-label=""
                  :is-multiple="false"
                  track-by="name"
                  label="name"
                  :options="serviceList"
                  :searchable="false"
                  :allow-empty="false"
                  :custom-label="serviceCustomLabel"
                >
                </BaseInputSelect>
              </div>
            </div>
            <div class="col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1">
              <FreeStyleShimmer
                height="38px"
                width="100%"
                class="mt-10"
                v-if="isLoadingFlag"
              />
              <div v-else>
                <BaseInputSelect
                  :has-label="true"
                  :title="$t('label.priority')"
                  class="primary"
                  v-model="requestParams.priority"
                  name="priority"
                  deselect-label=""
                  :is-multiple="false"
                  track-by="id"
                  label="name"
                  :options="priorityList"
                  :searchable="false"
                  :allow-empty="false"
                  :is-preselect-first="true"
                >
                </BaseInputSelect>
              </div>
            </div>
            <div class="col-span-2">
              <div class="col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1">
                <FreeStyleShimmer
                  height="100px"
                  width="100%"
                  class="mt-10"
                  v-if="isLoadingFlag"
                />
                <InputTextarea
                  v-else
                  :has-label="true"
                  :label="$t('label.description')"
                  id="validation-form-6"
                  v-model="requestParams.description"
                  :rows="'5'"
                  name="description"
                  :placeholder="''"
                ></InputTextarea>
              </div>
            </div>
            <div class="col-span-2">
              <div
                class="col-span-2 md:col-span-1 lg:col-span-1 xl:col-span-1 relative"
              >
                <FreeStyleShimmer
                  height="100px"
                  width="100%"
                  class="mt-10"
                  v-if="isLoadingFlag"
                />

                <input
                  ref="fileInput"
                  @change="setFile"
                  type="file"
                  class="block w-full border border-gray-200 shadow-sm rounded-lg text-sm focus:z-10 focus:border-blue-500 focus:ring-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:bg-neutral-900 dark:border-neutral-700 dark:text-neutral-400 file:bg-gray-50 file:border-0 file:me-4 file:py-3 file:px-4 dark:file:bg-neutral-700 dark:file:text-neutral-400"
                />
                <button
                  v-if="requestParams.file"
                  @click="clearFile"
                  class="absolute inline-flex left-1 top-1 bottom-0 rounded-[50%] justify-center place-items-center w-4 h-4 border-none appearance-none m-0 bg-gray-400 hover:bg-gray-700 p-0.5 cursor-pointer text-white size-3"
                >
                  &times;
                </button>
              </div>
            </div>
          </div>
        </Form>
      </div>

      <template v-slot:footer>
        <div class="flex col-md-12 items-end justify-end gap-2">
          <Button
            variant="outline-secondary"
            @click="close"
            size="sm"
            class="shadow-md justify-end items-end"
          >
            {{ $t("button.cancel") }}
          </Button>
          <Button
            variant="primary"
            size="sm"
            form="myForm"
            class="shadow-md justify-end items-end"
          >
            {{ $t("button.add-request") }}
          </Button>
        </div>
      </template>
    </BaseDialog>
  </section>
</template>

<style scoped></style>
